import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Compass, MapPin, ArrowRight, Navigation, Clock, Volume2, VolumeX, Play, Square } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useLang } from "@/context/LangContext";
import { useProgress } from "@/context/ProgressContext";
import { useAlerts } from "@/context/AlertsContext";
import { usePrayerSchedule } from "@/context/PrayerScheduleContext";
import { compass8, fmtKm } from "@/lib/geo";
import { fill } from "@/lib/utils";
import { PRAYERS, fmtCountdown, fmtTime, muezzinById, readMuezzin, adhanSoundOn } from "@/lib/prayerTimes";
import { HomeLocation } from "@/components/HomeLocation";
import { HomePrayerTimes } from "@/components/HomePrayerTimes";
import { HomeWeather } from "@/components/HomeWeather";
import { WellnessTips } from "@/components/WellnessTips";
import LiveQiblaCompass from "@/components/LiveQiblaCompass";

const SOON_MS = 15 * 60 * 1000;

// Static mini dial: needle = Qibla bearing measured clockwise from north.
function MiniDial({ bearing, deg }) {
  return (
    <div className="relative h-24 w-24 shrink-0 rounded-full border-2 border-primary/25 bg-gradient-to-b from-secondary to-card" data-testid="qibla-widget-dial">
      {["N", "E", "S", "W"].map((d, i) => (
        <span key={d} className="absolute left-1/2 top-1/2 text-[9px] font-semibold text-muted-foreground" style={{ transform: `translate(-50%, -50%) rotate(${i * 90}deg) translateY(-40px) rotate(${-i * 90}deg)` }}>{d}</span>
      ))}
      <div className="absolute inset-0 transition-transform duration-700 ease-out" style={{ transform: `rotate(${bearing ?? 0}deg)` }} data-testid="qibla-widget-needle" data-angle={deg ?? undefined}>
        <div className="absolute left-1/2 top-2.5 -translate-x-1/2 flex flex-col items-center">
          <img src="/kaaba-qibla.png" alt="" className="h-5 w-5 rounded-sm shadow" />
          <div className="mt-0.5 h-0 w-0 border-x-[4px] border-x-transparent border-b-[9px] border-b-primary" />
        </div>
      </div>
      <div className="absolute left-1/2 top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[hsl(var(--gold))]" />
    </div>
  );
}

// Dashboard card: current location → weather + heat tips → Qibla mini compass (tap = full live compass)
// + next-prayer countdown with automatic adhan (favourite muezzin) → today's prayer times.
// Every value comes from the shared PrayerScheduleContext, so it always matches the full /times page.
export function HomePrayerCard() {
  const { t } = useTranslation();
  const { tc, lang } = useLang();
  const { progress } = useProgress();
  const alerts = useAlerts();
  const { pos, located, place, placeLoading, locate, now, list: times, next, remaining, qiblaBearing, km, tz, tzOffset, deviceOffset, tzDiffers } = usePrayerSchedule();
  const [autoAdhan, setAutoAdhan] = useState(adhanSoundOn);
  const adhan = alerts.adhan; // shared app-wide player (the pop-up fires it at prayer time)

  const bearing = pos ? qiblaBearing : null;
  const deg = bearing == null ? null : Math.round(bearing);
  const ready = located !== null;
  const soon = remaining != null && remaining <= SOON_MS;
  const labelOf = (key) => tc(PRAYERS.find((p) => p.key === key)?.label);
  const nextTime = next ? fmtTime(next.at, lang, tz) : "";
  const muezzinId = readMuezzin(progress);

  const toggleAuto = () => { const v = !autoAdhan; setAutoAdhan(v); localStorage.setItem("adzan_sound", v ? "on" : "off"); if (!v) adhan.stop(); };

  return (
    <Card className="rounded-3xl border-[hsl(var(--gold))]/40 bg-[hsl(var(--gold))]/5 shadow-sm p-5 space-y-4" data-testid="home-prayer-card">
      <HomeLocation pos={pos} located={located} place={place} placeLoading={placeLoading} onRefresh={() => locate(true)} />
      {pos && (
        <div className="border-t border-border/60 pt-4">
          <HomeWeather pos={pos} now={now} maghribAt={times.find((p) => p.key === "maghrib")?.at} />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-5 border-t border-border/60 pt-4">
        <Dialog>
          <DialogTrigger asChild>
            <button type="button" className="group flex min-w-0 flex-1 items-center gap-5 text-start" data-testid="qibla-widget" aria-label={tc({ id: "Buka kompas kiblat", en: "Open Qibla compass", ar: "افتح بوصلة القبلة" })}>
              <MiniDial bearing={bearing} deg={deg} />
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-[hsl(var(--gold))]">
                  <Compass className="h-3.5 w-3.5" /> {t("qibla")}
                </p>
                <p className="mt-0.5 font-serif text-3xl tabular-nums leading-tight" data-testid="qibla-widget-bearing">
                  {deg == null ? "—" : <>{deg}&deg; <span className="text-base text-muted-foreground font-sans">{compass8(deg)}</span></>}
                </p>
                <p className="text-xs text-muted-foreground truncate" data-testid="qibla-widget-distance">
                  {located === null
                    ? tc({ id: "Mencari lokasi Anda…", en: "Finding your location…", ar: "جارٍ تحديد موقعك…" })
                    : pos
                      ? <><MapPin className="inline h-3 w-3 me-1 -mt-0.5" />{fill(tc({ id: "{km} km ke Ka'bah", en: "{km} km to the Ka'bah", ar: "{km} كم إلى الكعبة" }), { km: fmtKm(km, lang) })}</>
                      : tc({ id: "Izinkan akses lokasi untuk arah yang akurat", en: "Allow location access for an accurate bearing", ar: "اسمح بالوصول إلى الموقع للحصول على اتجاه دقيق" })}
                </p>
                <p className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-primary" data-testid="qibla-widget-open">
                  <Navigation className="h-3.5 w-3.5" />
                  {tc({ id: "Ketuk untuk kompas penuh", en: "Tap for the full compass", ar: "اضغط لفتح البوصلة الكاملة" })}
                  <ArrowRight className="h-4 w-4 rtl:-scale-x-100 transition-transform group-hover:translate-x-0.5" />
                </p>
              </div>
            </button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-sm" data-testid="qibla-widget-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Compass className="h-5 w-5 text-primary" />{tc({ id: "Kompas Kiblat Live", en: "Live Qibla Compass", ar: "بوصلة القبلة المباشرة" })}</DialogTitle>
              <DialogDescription className="sr-only">{tc({ id: "Kompas kiblat langsung dengan pengumuman suara dan getaran saat menghadap kiblat", en: "Live Qibla compass with voice and haptic guidance when facing the Qibla", ar: "بوصلة قبلة مباشرة مع إعلان صوتي واهتزاز عند استقبال القبلة" })}</DialogDescription>
            </DialogHeader>
            {next && (
              <p className="-mt-1 text-center text-xs text-muted-foreground" data-testid="qibla-dialog-next">
                {t("nextPrayer")}: <b className="text-foreground">{labelOf(next.key)}</b> · {nextTime} · <span className="tabular-nums">{fmtCountdown(remaining)}</span>
              </p>
            )}
            <LiveQiblaCompass />
          </DialogContent>
        </Dialog>

        {next && (
          <div
            className={`flex w-full sm:w-auto sm:min-w-[12rem] flex-col rounded-2xl px-4 py-3 transition-colors ${soon ? "bg-amber-500/15 ring-1 ring-amber-500/40" : "bg-card/70 ring-1 ring-border/60"}`}
            data-testid="qibla-widget-prayer" data-prayer={next.key} data-soon={soon || undefined} data-at={next.at.getTime()}
          >
            <p className={`flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide ${soon ? "text-amber-600 dark:text-amber-400" : "text-muted-foreground"}`}>
              <Clock className={`h-3.5 w-3.5 ${soon ? "animate-pulse" : ""}`} />
              {soon ? tc({ id: "Segera menghadap kiblat", en: "Face the Qibla soon", ar: "استقبل القبلة قريبًا" }) : t("nextPrayer")}
            </p>
            <p className="mt-0.5 text-sm font-medium" data-testid="qibla-widget-next">
              {labelOf(next.key)} <span className="text-muted-foreground tabular-nums">· {nextTime}</span>
            </p>
            <p className={`font-serif text-2xl tabular-nums leading-tight ${soon ? "text-amber-600 dark:text-amber-400" : "text-primary"}`} data-testid="qibla-widget-countdown">
              {fmtCountdown(remaining)}
            </p>

            <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="qibla-adhan-controls">
              <button
                onClick={toggleAuto} aria-pressed={autoAdhan} data-testid="qibla-adhan-toggle"
                title={tc({ id: "Adzan otomatis saat waktu salat tiba", en: "Play the adhan automatically at prayer time", ar: "تشغيل الأذان تلقائيًا عند دخول وقت الصلاة" })}
                className={`inline-flex h-9 items-center gap-1.5 rounded-full border px-3 text-xs font-medium transition-colors ${autoAdhan ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-secondary"}`}
              >
                {autoAdhan ? <Volume2 className="h-3.5 w-3.5" /> : <VolumeX className="h-3.5 w-3.5" />}
                {tc({ id: "Adzan otomatis", en: "Auto adhan", ar: "أذان تلقائي" })}
              </button>
              {adhan.playing ? (
                <button onClick={adhan.stop} data-testid="qibla-adhan-stop" className="inline-flex h-9 items-center gap-1.5 rounded-full bg-primary px-3 text-xs font-medium text-white hover:opacity-90">
                  <Square className="h-3 w-3 fill-white" />{tc({ id: "Hentikan", en: "Stop", ar: "إيقاف" })}
                </button>
              ) : adhan.blocked ? (
                <button onClick={() => adhan.play(adhan.blocked, muezzinId)} data-testid="qibla-adhan-play" className="inline-flex h-9 items-center gap-1.5 rounded-full bg-amber-500 px-3 text-xs font-medium text-white hover:opacity-90 animate-pulse">
                  <Play className="h-3 w-3 fill-white" />{tc({ id: "Ketuk untuk memutar adzan", en: "Tap to play the adhan", ar: "اضغط لتشغيل الأذان" })}
                </button>
              ) : (
                <button onClick={() => adhan.play(next.key, muezzinId)} data-testid="qibla-adhan-preview" title={tc(muezzinById(muezzinId).label)} className="inline-flex h-9 items-center gap-1.5 rounded-full border border-border px-3 text-xs font-medium text-muted-foreground hover:bg-secondary">
                  <Play className="h-3 w-3" />{tc({ id: "Uji adzan", en: "Test adhan", ar: "تجربة الأذان" })}
                </button>
              )}
            </div>
            <p className="mt-1.5 truncate text-[11px] text-muted-foreground" data-testid="qibla-adhan-status" data-playing={adhan.playing || undefined}>
              {adhan.playing
                ? fill(tc({ id: "Adzan {prayer} berkumandang…", en: "{prayer} adhan is playing…", ar: "أذان {prayer} يُرفع الآن…" }), { prayer: labelOf(adhan.playing) })
                : tc(muezzinById(muezzinId).label)}
            </p>
          </div>
        )}
      </div>

      {ready && (
        <div className="border-t border-border/60 pt-4">
          <HomePrayerTimes times={times} nextKey={next?.key} now={now} fallback={!pos} tz={tz} tzHint={tzDiffers ? { tzOffset, deviceOffset } : null} />
        </div>
      )}

      {/* Diet & exercise guidance — always reachable, even without location access */}
      <div className="border-t border-border/60 pt-4">
        <WellnessTips />
      </div>
    </Card>
  );
}
